import React, { useState } from 'react';
import { X, Plus, Edit2, Trash2, Loader2 } from 'lucide-react';
import ImageUploadV2 from '../common/ImageUploadV2';

const ManageCategoriesModal = ({ event, onClose, onSaved }) => {
    const [categories, setCategories] = useState(
        event.participantCategories ? [...event.participantCategories] : []
    );
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [amenityInput, setAmenityInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const isAddingNew = editingId && !categories.find(c => c.id === editingId);

    const startAdd = () => {
        const newId = `cat-${Date.now()}`;
        setEditForm({ id: newId, name: '', description: '', idCardBackground: '', amenities: [], order: categories.length });
        setEditingId(newId);
        setAmenityInput('');
    };

    const startEdit = (cat) => {
        setEditForm({ ...cat, amenities: [...(cat.amenities || [])] });
        setEditingId(cat.id);
        setAmenityInput('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
        setAmenityInput('');
    };

    const saveEdit = () => {
        if (!editForm.name.trim()) return;
        const existing = categories.find(c => c.id === editForm.id);
        if (existing) {
            setCategories(cats => cats.map(c => c.id === editForm.id ? editForm : c));
        } else {
            setCategories(cats => [...cats, editForm]);
        }
        cancelEdit();
    };

    const deleteCategory = (id) => {
        if (!window.confirm('Delete this category? Existing participants with this category will lose their category assignment.')) return;
        setCategories(cats => cats.filter(c => c.id !== id));
    };

    const addAmenity = () => {
        const val = amenityInput.trim();
        if (!val) return;

        const newAmenities = val.split(',').map(s => s.trim()).filter(Boolean);
        
        setEditForm(prev => {
            const currentAmenities = prev.amenities || [];
            const toAdd = newAmenities.filter(a => !currentAmenities.includes(a));
            if (toAdd.length === 0) return prev;
            return {
                ...prev,
                amenities: [...currentAmenities, ...toAdd]
            };
        });
        
        setAmenityInput('');
    };

    const removeAmenity = (amenity) => {
        setEditForm(prev => ({ ...prev, amenities: prev.amenities.filter(a => a !== amenity) }));
    };

    const handleSave = async () => {
        if (editingId) {
            setError('Please save or cancel the current edit before saving.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...event, participantCategories: categories })
                }
            );
            const data = await response.json();
            if (data.success) {
                onSaved(categories);
                onClose();
            } else {
                setError(data.message || 'Failed to save categories');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Manage Categories</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Each category gets its own ID card background and amenities.</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {/* Add button */}
                    <button
                        type="button"
                        onClick={startAdd}
                        disabled={!!editingId}
                        className="w-full inline-flex items-center justify-center px-4 py-2.5 border-2 border-dashed border-blue-300 text-blue-600 text-sm font-medium rounded-xl hover:border-blue-400 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Category
                    </button>

                    {/* New category inline form */}
                    {isAddingNew && (
                        <CategoryForm
                            form={editForm}
                            setForm={setEditForm}
                            amenityInput={amenityInput}
                            setAmenityInput={setAmenityInput}
                            onSave={saveEdit}
                            onCancel={cancelEdit}
                            onAddAmenity={addAmenity}
                            onRemoveAmenity={removeAmenity}
                            isNew
                        />
                    )}

                    {/* Empty state */}
                    {categories.length === 0 && !isAddingNew && (
                        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                            <p className="text-gray-500 font-medium">No categories yet</p>
                            <p className="text-gray-400 text-sm mt-1">
                                Click "Add New Category" to get started.
                            </p>
                        </div>
                    )}

                    {/* Category list */}
                    {categories.map(cat => (
                        <div key={cat.id} className="border border-gray-200 rounded-xl overflow-hidden">
                            {editingId === cat.id ? (
                                <CategoryForm
                                    form={editForm}
                                    setForm={setEditForm}
                                    amenityInput={amenityInput}
                                    setAmenityInput={setAmenityInput}
                                    onSave={saveEdit}
                                    onCancel={cancelEdit}
                                    onAddAmenity={addAmenity}
                                    onRemoveAmenity={removeAmenity}
                                    isNew={false}
                                />
                            ) : (
                                <div className="p-4 flex items-start gap-4 bg-white">
                                    {/* Thumbnail */}
                                    <div className="flex-shrink-0 w-12 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                                        {cat.idCardBackground ? (
                                            <img src={cat.idCardBackground} alt={cat.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="text-gray-400 text-xs text-center px-1">No BG</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900">{cat.name}</p>
                                        {cat.description && <p className="text-sm text-gray-500 mt-0.5">{cat.description}</p>}
                                        {cat.amenities?.length > 0 ? (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {cat.amenities.map(a => (
                                                    <span key={a} className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">{a}</span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 mt-1">No amenities</p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => startEdit(cat)}
                                            disabled={!!editingId}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-40"
                                            title="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteCategory(cat.id)}
                                            disabled={!!editingId}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="inline-flex items-center px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {saving ? 'Saving...' : 'Save Categories'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const CategoryForm = ({ form, setForm, amenityInput, setAmenityInput, onSave, onCancel, onAddAmenity, onRemoveAmenity, isNew }) => (
    <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl space-y-4">
        <h3 className="font-semibold text-gray-900">{isNew ? 'New Category' : 'Edit Category'}</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    value={form.name || ''}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Delegate, Speaker, Volunteer"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                    type="text"
                    value={form.description || ''}
                    onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional short description"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
            </div>
        </div>

        <ImageUploadV2
            onUploadSuccess={url => setForm(prev => ({ ...prev, idCardBackground: url || '' }))}
            onUploadError={err => console.error('Upload error:', err)}
            uploadEndpoint="/api/v2/events/upload/event-background"
            currentImageUrl={form.idCardBackground || null}
            label="ID Card Background for this Category"
        />

        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
            <div className="flex gap-2 mb-2">
                <input
                    type="text"
                    value={amenityInput}
                    onChange={e => setAmenityInput(e.target.value)}
                    onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); onAddAmenity(); } }}
                    placeholder="e.g., Lunch, Wifi, Kit Bag"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
                <button
                    type="button"
                    onClick={onAddAmenity}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                    Add
                </button>
            </div>
            {form.amenities?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {form.amenities.map(a => (
                        <span key={a} className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full border border-emerald-100">
                            {a}
                            <button type="button" onClick={() => onRemoveAmenity(a)} className="ml-2 text-emerald-400 hover:text-emerald-600">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-gray-400 italic">No amenities yet.</p>
            )}
        </div>

        <div className="flex gap-3">
            <button
                type="button"
                onClick={onSave}
                disabled={!form.name?.trim()}
                className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {isNew ? 'Add Category' : 'Save Changes'}
            </button>
            <button
                type="button"
                onClick={onCancel}
                className="px-5 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-white"
            >
                Cancel
            </button>
        </div>
    </div>
);

export default ManageCategoriesModal;
